import { Button, Stack, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isValidEmail } from 'utils/general/isValidEmail'

export interface Props {
  email: string
  credentialsError: string | null
  onEmailChange: (val: string) => void
  onSubmit: () => Promise<void>
}

export const LoginEmailContainer = ({ email, credentialsError, onEmailChange, onSubmit }: Props) => {
  const { t } = useTranslation('login')
  const [typedEmail, setTypedEmail] = useState(email)
  const [emailError, setEmailError] = useState<null | string>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const validateEmail = () => {
    if (typedEmail === '') {
      setEmailError('validation.blank-email')
      return false
    }
    if (!isValidEmail(typedEmail)) {
      setEmailError('validation.invalid-email')
      return false
    }
    setEmailError(null)
    return true
  }

  useEffect(() => {
    if (email) setTypedEmail(email)
  }, [email])

  useEffect(() => {
    setEmailError(null)
  }, [typedEmail])

  const handleSubmit = async () => {
    if (!validateEmail()) {
      return
    }
    setIsSubmitting(true)
    try {
      await onSubmit()
    } catch (e) {
      enqueueSnackbar(t('email.error-submitting'), { variant: 'error' })
      console.error('error submitting login email request', { e })
    }
    setIsSubmitting(false)
  }

  const getErrorMessage = () => {
    if (emailError) return t(emailError)
    if (credentialsError) return credentialsError
    return ''
  }

  const handleEmailChanged = (newValue: string) => {
    setEmailError(null)
    setTypedEmail(newValue)
    onEmailChange(newValue)
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit()
      }}
    >
      <Stack spacing={0}>
        <Stack>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <Typography suppressHydrationWarning variant="H5Semibold">
                {t('email.title')}
              </Typography>
              <Typography suppressHydrationWarning variant="Body14pxRegular" color="neutralLight.900">
                {t('email.subtitle')}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="email"
                  name="email"
                  label={t('email-label')}
                  value={typedEmail}
                  suppressHydrationWarning
                  onChange={(e) => {
                    handleEmailChanged(e.target.value)
                  }}
                  error={Boolean(credentialsError) || Boolean(emailError)}
                  onBlur={validateEmail}
                  slotProps={{
                    htmlInput: {
                      'data-testid': 'input-email',
                    },
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography suppressHydrationWarning variant="Body12pxRegular" color="error.main" align="left">
                  {getErrorMessage()}
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Button
                loading={isSubmitting}
                size="medium"
                data-testid="btn-login"
                type="submit"
                color="secondary"
                variant="contained"
                fullWidth
                suppressHydrationWarning
              >
                {t('login-button')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}
